import { auth, db } from '../../../shared/storage/firebase';
import { doc, updateDoc, getDoc, collection, addDoc, query, where, getDocs, setDoc, orderBy, limit as limitData, deleteDoc, collectionGroup, increment } from 'firebase/firestore';
import { RHYTHM_RALLY_ACHIEVEMENTS } from '../data/rhythmRallyAchievements';
import { RHYTHM_PUZZLE_ACHIEVEMENTS } from '../data/rhythmPuzzleAchievements';
import { EmailService } from '../../../services/EmailService';
import { User } from '../../../types';
import { ensureAnonymousAuth } from '../../../shared/storage/firebase';
import { StudentAuthService } from '../../../shared/storage/StudentAuthService';

// Types for game communication
export interface GameReward {
  museCash: number;
  achievements?: string[];
  muses?: string[];
}

export interface GameProgress {
  gameId: string;
  level: number;
  score: number;
  completed: boolean;
  timeSpent: number;
}

export interface SavedSong {
  id?: string;
  title: string;
  gameId: string;
  userId?: string;
  audioData?: string; // Base64 encoded audio data (for small files)
  audioUrl?: string; // URL to stored audio file (for large files)
  format?: string; // 'wav', 'mp3', 'ogg', etc.
  duration?: number; // Song length in seconds
  sampleRate?: number; // Audio sample rate (44100, 48000, etc.)
  channels?: number; // Number of audio channels (1=mono, 2=stereo)
  fileSize?: number; // File size in bytes
  createdAt: Date;
  updatedAt?: Date;
  isPublic?: boolean;
  tags?: string[];
  description?: string;
  bpm?: number; // Beats per minute
  key?: string; // Musical key (C, D, E, etc.)
  genre?: string; // Music genre
  stats?: {
    plays: number;
    likes: number;
    downloads: number;
  };
}

export interface Achievement {
  id: string;
  userId: string;
  achievementId: string;
  gameId?: string;
  unlockedAt: Date;
  progress?: number;
}

export interface GameStats {
  userId: string;
  gameId: string;
  completions: number;
  bestScore: number;
  totalTimePlayed: number;
  achievements: string[];
  lastPlayed: Date;
  totalCashEarned?: number;
  progressData?: any;
}

// Achievement Definition (Site Side)
export interface AchievementDefinition {
  id: string;
  gameId: string;
  title: string;
  description?: string;
  iconUrl?: string;
  museCashReward: number;
  type?: string;
  rarity?: 'common' | 'rare' | 'epic' | 'legendary';
  implemented: boolean;
  expectedVersion?: string;
  sortOrder?: number;
  category?: string;

  hint?: string;
  frequency?: 'daily' | 'once';
}

// Standard Daily Achievements
export const DAILY_ACHIEVEMENTS: AchievementDefinition[] = [
  {
    id: 'daily-play',
    gameId: 'any', // Placeholder, will be replaced
    title: 'Daily Practice',
    description: 'Play this game today.',
    iconUrl: '📅',
    museCashReward: 10,
    type: 'daily',
    rarity: 'common',
    implemented: true,
    frequency: 'daily',
    sortOrder: -2, // Show first
    category: 'daily'
  },
  {
    id: 'daily-time-5m',
    gameId: 'any', // Placeholder
    title: '5 Minute Master',
    description: 'Play for 5 minutes today.',
    iconUrl: '⏱️',
    museCashReward: 25,
    type: 'daily',
    rarity: 'common',
    implemented: true,
    frequency: 'daily',
    sortOrder: -1, // Show second
    category: 'daily'
  }
];

// Game Event Payload (Game Side)
export interface GameEventPayload {
  eventType: 'achievement' | 'song_save' | 'progress' | 'completion';
  payload: {
    id?: string;
    title?: string;
    museCashReward?: number;
    songData?: any;
    score?: number;
    achievements?: string[];
  };
}

class GameAPI {
  // Track pending achievements to prevent race conditions
  private pendingAchievements = new Set<string>();

  /**
   * Resolves the current user ID, handling both regular auth and anonymous student auth
   * For regular users: returns auth.currentUser.uid
   * For anonymous students: returns the real student ID from session mapping
   */
  async getCurrentUserId(): Promise<string | null> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return null;
    }

    // If user is anonymous, try to get real student ID from session mapping
    if (currentUser.isAnonymous) {
      try {
        const studentData = await StudentAuthService.getCurrentStudentData();
        if (studentData) {
          console.log(`🔄 Resolved anonymous user ${currentUser.uid} → student ${studentData.id}`);
          return studentData.id;
        }
        console.warn(`Anonymous user ${currentUser.uid} has no session mapping`);
        return null;
      } catch (error) {
        console.error('Error resolving student session:', error);
        return null;
      }
    }

    // Regular authenticated user (teacher)
    return currentUser.uid;
  }

  // Enhanced achievement system with validation
  async awardAchievement(gameId: string, achievementId: string): Promise<boolean> {
    await ensureAnonymousAuth();
    const uid = await this.getCurrentUserId();
    if (!uid) {
      console.warn('User not authenticated for achievement award');
      return false;
    }

    // Create unique key for this user-achievement combination
    const pendingKey = `${uid}:${gameId}:${achievementId}`;
    
    // Check if this achievement is already being processed
    if (this.pendingAchievements.has(pendingKey)) {
      console.log(`⏳ Achievement ${achievementId} already being processed for user ${uid}, skipping to prevent race condition`);
      return false;
    }

    try {
      // Mark as pending to prevent concurrent processing
      this.pendingAchievements.add(pendingKey);
      console.log(`🔒 Achievement ${achievementId} marked as pending for user ${uid}`);

      // Check if achievement definition exists
      const definition = await this.getAchievementDefinition(gameId, achievementId);
      if (!definition) {
        console.warn(`Achievement definition not found: ${gameId}/${achievementId}`);
        this.pendingAchievements.delete(pendingKey);
        return false;
      }

      // Check if user already has this achievement
      const hasAchievement = await this.userHasAchievement(uid, gameId, achievementId);

      // Determine if this achievement should behave as "daily-resettable"
      // NOTE:
      // - Global standard dailies (`daily-play`, `daily-time-5m`) use `frequency: 'daily'`
      // - Game-specific dailies (e.g. Rhythm Rally level_1) may use `type: 'daily'`
      // We treat either as a daily achievement for re-award behavior.
      const isDailyAchievement =
        definition.frequency === 'daily' || definition.type === 'daily';

      // For daily achievements, check if it was earned TODAY
      if (hasAchievement && isDailyAchievement) {
        const lastEarned = await this.getAchievementUnlockedAt(uid, gameId, achievementId);
        if (lastEarned) {
          const today = new Date();
          const earnedDate = new Date(lastEarned);
          
          if (today.getDate() === earnedDate.getDate() && 
              today.getMonth() === earnedDate.getMonth() && 
              today.getFullYear() === earnedDate.getFullYear()) {
            console.log(`Daily achievement ${achievementId} already earned today`);
            this.pendingAchievements.delete(pendingKey);
            return false;
          }
          // If earned on a previous day, we allow re-awarding!
          console.log(`Daily achievement ${achievementId} earned previously on ${earnedDate.toDateString()}, awarding for today!`);
        }
      } else if (hasAchievement) {
        console.log(`User already has achievement: ${achievementId}`);
        this.pendingAchievements.delete(pendingKey);
        return false;
      }

      // Award achievement using definition data
      console.log(`🏆 AWARDING ACHIEVEMENT: ${achievementId} to user ${uid}`);
      await this.recordUserAchievement(uid, gameId, achievementId);
      // Note: Muse cash is now handled by the frontend event listener for immediate UI updates
      
      // Dispatch custom event for proper achievement
      const today = new Date();
      const dateKey = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
      const eventId = `${gameId}:${achievementId}:${dateKey}`;
      const achievementEvent = new CustomEvent('achievementUnlocked', {
        detail: {
          achievement: {
            title: definition.title,
            iconUrl: definition.iconUrl || '🏆',
            museCashReward: definition.museCashReward,
            rarity: definition.rarity || 'common',
            description: definition.description
          },
          reward: definition.museCashReward,
          gameId,
          achievementId,
          eventId
        }
      });
      console.log('🎯 DISPATCHING ACHIEVEMENT EVENT:', achievementEvent.detail);
      window.dispatchEvent(achievementEvent);
      
      console.log(`Achievement unlocked: ${definition.title} (+${definition.museCashReward} cash)`);
      
      // Clear pending state on success
      this.pendingAchievements.delete(pendingKey);
      console.log(`✅ Achievement ${achievementId} processing completed for user ${uid}, removed from pending`);
      
      return true;

    } catch (error) {
      console.error('Error awarding achievement:', error);
      
      // Clear pending state on error
      this.pendingAchievements.delete(pendingKey);
      console.log(`❌ Achievement ${achievementId} processing failed for user ${uid}, removed from pending`);
      
      return false;
    }
  }

  // Get achievement definition from site-side store
  async getAchievementDefinition(gameId: string, achievementId: string): Promise<AchievementDefinition | null> {
    // Check standard daily achievements first
    const daily = DAILY_ACHIEVEMENTS.find(d => d.id === achievementId);
    if (daily) {
      return { ...daily, gameId };
    }

    try {
      const defRef = doc(db, 'achievementDefinitions', gameId, 'achievements', achievementId);
      const defDoc = await getDoc(defRef);
      
      if (defDoc.exists()) {
        return defDoc.data() as AchievementDefinition;
      }
      return null;
    } catch (error) {
      console.error('Error fetching achievement definition:', error);
      return null;
    }
  }

  // Get achievement definition from site-side store (with daily injection)
  async getGameAchievementDefinitions(gameId: string): Promise<AchievementDefinition[]> {
    try {
      // 1. Get game-specific achievements
      const achievementsRef = collection(db, 'achievementDefinitions', gameId, 'achievements');
      const snapshot = await getDocs(achievementsRef);
      const gameAchievements = snapshot.docs.map(doc => doc.data() as AchievementDefinition);

      // 2. Inject standard daily achievements
      const dailyAchievements = DAILY_ACHIEVEMENTS.map(daily => ({
        ...daily,
        gameId: gameId // Bind to this game
      }));

      return [...dailyAchievements, ...gameAchievements];
    } catch (error) {
      console.error('Error fetching game achievement definitions:', error);
      return [];
    }
  }

  // Helper to get unlock date
  async getAchievementUnlockedAt(userId: string, gameId: string, achievementId: string): Promise<Date | null> {
    try {
      const composedId = `${gameId}:${achievementId}`;
      const achievementRef = doc(db, 'users', userId, 'achievements', composedId);
      const docSnap = await getDoc(achievementRef);
      if (docSnap.exists()) {
        return docSnap.data().unlockedAt?.toDate() || null;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  // Create/update achievement definition (for admin/dev use)
  async setAchievementDefinition(gameId: string, definition: AchievementDefinition): Promise<void> {
    await ensureAnonymousAuth();
    try {
      const defRef = doc(db, 'achievementDefinitions', gameId, 'achievements', definition.id);
      await setDoc(defRef, {
        ...definition,
        gameId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      
      console.log(`Achievement definition saved: ${gameId}/${definition.id}`);
    } catch (error) {
      console.error('Error saving achievement definition:', error);
      throw error;
    }
  }

  // Check if user has specific achievement
  async userHasAchievement(userId: string, gameId: string, achievementId: string): Promise<boolean> {
    try {
      const composedId = `${gameId}:${achievementId}`;
      console.log(`🔍 CHECKING: Does user ${userId} have achievement ${composedId}?`);
      
      const achievementRef = doc(db, 'users', userId, 'achievements', composedId);
      const docSnap = await getDoc(achievementRef);
      
      const hasAchievement = docSnap.exists();
      console.log(`🔍 RESULT: User ${userId} ${hasAchievement ? 'ALREADY HAS' : 'DOES NOT HAVE'} achievement ${composedId}`);
      
      return hasAchievement;
    } catch (error) {
      console.error('Error checking user achievement:', error);
      return false;
    }
  }

  // Record user achievement
  private async recordUserAchievement(userId: string, gameId: string, achievementId: string): Promise<void> {
    await ensureAnonymousAuth();
    console.log(`💾 SAVING USER ACHIEVEMENT: ${userId} unlocked ${achievementId} in ${gameId}`);
    
    // Debug: Log current auth state
    const currentUser = auth.currentUser;
    console.log(`🔍 Current auth state:`, {
      uid: currentUser?.uid,
      isAnonymous: currentUser?.isAnonymous,
      targetUserId: userId,
      isWritingToSelf: currentUser?.uid === userId
    });
    
    const composedId = `${gameId}:${achievementId}`;
    const achievementRef = doc(db, 'users', userId, 'achievements', composedId);
    const achievementData = {
      gameId,
      achievementId,
      unlockedAt: new Date(),
    };
    
    console.log(`💾 Achievement data to save:`, achievementData);
    console.log(`📍 Achievement path: users/${userId}/achievements/${composedId}`);
    
    try {
      await setDoc(achievementRef, achievementData);
      console.log(`✅ USER ACHIEVEMENT SAVED: ${achievementId}`);
    } catch (error) {
      console.error(`❌ FAILED TO SAVE USER ACHIEVEMENT:`, error);
      console.error(`❌ Error details:`, {
        code: (error as any)?.code,
        message: (error as any)?.message,
        userId,
        achievementId,
        currentUserUid: currentUser?.uid,
        isAnonymous: currentUser?.isAnonymous
      });
      throw error;
    }
  }

  // Add muse cash to user (atomic)
  private async addMuseCash(userId: string, amount: number): Promise<void> {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      museCash: increment(amount),
    });
  }

  // Main game event handler - this is what games call
  async onOvertureGameEvent(event: GameEventPayload): Promise<void> {
    const uid = await this.getCurrentUserId();
    if (!uid) {
      console.warn('User not authenticated for game event');
      return;
    }

    try {
      switch (event.eventType) {
        case 'achievement':
          if (event.payload.id) {
            await this.awardAchievement(
              this.currentGameId || 'unknown',
              event.payload.id,
            );
          }
          break;

        case 'completion':
          if (this.currentGameId) {
            await this.giveRewards(this.currentGameId, {
              museCash: event.payload.museCashReward || 50,
              achievements: event.payload.achievements || [],
            });
          }
          break;

        case 'song_save':
          if (event.payload.songData && this.currentGameId) {
            await this.saveSong({
              title: event.payload.songData.title || 'Untitled Song',
              gameId: this.currentGameId,
              audioData: event.payload.songData.data || JSON.stringify(event.payload.songData),
              createdAt: new Date(),
            });
          }
          break;

        default:
          console.warn('Unknown game event type:', event.eventType);
      }
    } catch (error) {
      console.error('Error handling game event:', error);
    }
  }

  // Store current game ID for context
  private currentGameId: string | null = null;

  // Set game context and auto-sync achievements
  async setCurrentGame(gameId: string): Promise<void> {
    this.currentGameId = gameId;
    
    // Auto-sync achievements for this game
    await this.autoSyncAchievements(gameId);
  }

  // Auto-sync achievements from code to Firebase
  private async autoSyncAchievements(gameId: string): Promise<void> {
    try {
      const uid = await this.getCurrentUserId();
      console.log(`🔄 Auto-syncing achievements for ${gameId}...`);
      console.log(`👤 Current user: ${uid ? 'Authenticated' : 'NOT AUTHENTICATED'}`);
      
      let achievementsToSync: AchievementDefinition[] = [];
      
      // Get achievements for this specific game
      switch (gameId) {
        case 'rhythm-rally':
          achievementsToSync = RHYTHM_RALLY_ACHIEVEMENTS;
          break;
        case 'rhythm-puzzle':
          achievementsToSync = RHYTHM_PUZZLE_ACHIEVEMENTS;
          break;
        // Add more games here:
        // case 'piano-clicker':
        //   achievementsToSync = PIANO_CLICKER_ACHIEVEMENTS;
        //   break;
        default:
          console.log(`No achievements defined for ${gameId}`);
          return;
      }

      // Check which achievements need to be synced
      for (const achievement of achievementsToSync) {
        const existing = await this.getAchievementDefinition(gameId, achievement.id);
        
        if (!existing) {
          console.log(`📝 Syncing new achievement: ${achievement.title}`);
          try {
            await this.setAchievementDefinition(gameId, achievement);
            console.log(`✅ Successfully synced: ${achievement.title}`);
          } catch (syncError) {
            console.error(`❌ Failed to sync ${achievement.title}:`, syncError);
          }
        } else {
          // Update if version changed (you can add version field later)
          console.log(`✅ Achievement already exists: ${achievement.title}`);
        }
      }
      
      console.log(`✅ Achievement sync complete for ${gameId}`);
      
    } catch (error) {
      console.error('Error auto-syncing achievements:', error);
    }
  }

  // Send rewards to player after game completion
  async giveRewards(gameId: string, rewards: GameReward): Promise<void> {
    await ensureAnonymousAuth();
    const uid = await this.getCurrentUserId();
    if (!uid) throw new Error('User not authenticated');

    const userRef = doc(db, 'users', uid);
    // Atomically increment cash and update summary fields
    await updateDoc(userRef, {
      museCash: increment(rewards.museCash),
      lastGamePlayed: gameId,
      lastRewardAmount: rewards.museCash,
    });

    // Record game completion in separate game stats collection
    await this.updateGameStats(gameId, rewards.museCash);
    
    // Process achievements using new system
    if (rewards.achievements) {
      for (const achievementId of rewards.achievements) {
        await this.awardAchievement(gameId, achievementId);
      }
    }
    
    console.log(`Rewarded ${rewards.museCash} Muse Cash for completing ${gameId}`);
  }

  // Save progress for a specific game
  async saveGameProgress(progress: GameProgress): Promise<void> {
    await ensureAnonymousAuth();
    const uid = await this.getCurrentUserId();
    if (!uid) throw new Error('User not authenticated');

    const progressRef = collection(db, 'users', uid, 'gameProgress');
    await addDoc(progressRef, {
      ...progress,
      updatedAt: new Date(),
    });
  }

  // Enhanced song saving with Firebase Storage support
  async saveSong(songData: SavedSong): Promise<string> {
    await ensureAnonymousAuth();
    const uid = await this.getCurrentUserId();
    if (!uid) throw new Error('User not authenticated');

    console.log(`🎵 SAVING SONG: "${songData.title}" for user ${uid}`);
    
    try {
      // Apply defaults for missing fields (with temporary title)
      const enrichedSongData = this.applySongDefaults(songData, uid);
      
      // Handle audio file storage FIRST
      let audioUrl = '';
      if (enrichedSongData.audioData) {
        audioUrl = await this.uploadAudioFile(enrichedSongData.audioData, enrichedSongData, uid);
        console.log(`🎵 Audio uploaded to: ${audioUrl}`);
        // Remove base64 data after upload to save space
        delete enrichedSongData.audioData;
      }

      // Check if we should show naming popup
      const shouldShowNaming = !songData.title || 
                              songData.title === 'Untitled Song' || 
                              songData.title === 'Click here to name song!' ||
                              songData.title.includes('Beat Decoder') || 
                              songData.title.includes('Level');
      
      if (shouldShowNaming && audioUrl) {
        console.log('🎵 Song needs naming - showing modal with audio URL');
        
        // Create a promise that will be resolved when the user names the song
        const songName = await new Promise<string>((resolve) => {
          const eventId = `song-naming-${Date.now()}-${Math.random()}`;
          
          const handleSongNamed = (event: CustomEvent) => {
            if (event.detail.eventId === eventId) {
              window.removeEventListener('songNamed', handleSongNamed as EventListener);
              resolve(event.detail.songName);
            }
          };
          
          window.addEventListener('songNamed', handleSongNamed as EventListener);
          
          // Emit event to show naming modal with audio URL
          window.dispatchEvent(new CustomEvent('songNeedsNaming', {
            detail: {
              eventId,
              audioUrl  // Pass the Firebase Storage URL instead of raw data
            }
          }));
        });
        
        // Update the song data with the user-provided name
        enrichedSongData.title = songName;
        console.log(`🎵 Song named: "${songName}"`);
      }

      // Save song metadata to Firestore
      const songsRef = collection(db, 'users', uid, 'songs');
      
      // Sanitize the data for Firestore (convert any custom objects to plain JS objects)
      const sanitizedSongData = this.sanitizeForFirestore(enrichedSongData);
      
      const fullSongDataForEmail = {
        ...sanitizedSongData,
        audioUrl,
        createdAt: new Date(),
        updatedAt: new Date(),
        stats: {
          plays: 0,
          likes: 0,
          downloads: 0,
        }
      };

      const songDoc = await addDoc(songsRef, fullSongDataForEmail);

      // Update user's song count and statistics
      await this.updateUserSongStats(uid);

      // After successfully saving the song, check if we need to send an email
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (userData.parentEmail) {
            EmailService.triggerSongCreationEmail({
              parentEmail: userData.parentEmail,
              studentName: userData.displayName || 'Your student',
              song: { ...fullSongDataForEmail, id: songDoc.id },
                              userStats: {
                  totalSongs: (userData.totalSongsCreated || 0) + 1,
                  totalTimePlayed: this.formatTimeForEmail(userData.totalTimePlayed || 0),
                  favoriteMuseName: userData.selectedMuse?.name || 'their first Muse'
                }
            });
          }
        }
      } catch (emailError) {
        console.error('Failed to send song creation email notification:', emailError);
      }
      
      console.log(`✅ SONG SAVED: "${songData.title}" with ID ${songDoc.id}`);
      console.log(`🔗 Audio URL: ${audioUrl}`);
      
      return songDoc.id;
    } catch (error) {
      console.error('❌ ERROR SAVING SONG:', error);
      throw error;
    }
  }

  // Apply default values for missing song fields
  private applySongDefaults(songData: SavedSong, userId: string): SavedSong {
    const now = new Date();
    const gameTitle = songData.gameId?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Game';
    
    // Ensure tags is a plain JavaScript array (Unity may send custom Array objects)
    let sanitizedTags: string[] = [songData.gameId || 'unknown', 'user-created'];
    if (songData.tags) {
      try {
        // Convert any array-like object to plain JavaScript array
        sanitizedTags = Array.isArray(songData.tags) 
          ? [...songData.tags] // Create new plain array from existing array
          : Array.from(songData.tags as ArrayLike<string>); // Convert array-like to array
      } catch (error) {
        console.warn('⚠️ Failed to parse tags from songData, using defaults:', error);
        sanitizedTags = [songData.gameId || 'unknown', 'user-created'];
      }
    }
    
    return {
      ...songData,
      userId,
      title: songData.title || 'Click here to name song!',
      format: songData.format || 'wav',
      isPublic: songData.isPublic || false,
      tags: sanitizedTags,
      description: songData.description || `Created in ${gameTitle}`,
      duration: songData.duration || 0,
      sampleRate: songData.sampleRate || 44100,
      channels: songData.channels || 2,
    };
  }

  // Upload audio file to Firebase Storage
  private async uploadAudioFile(base64Data: string, songData: SavedSong, userId: string): Promise<string> {
    try {
      // Import Firebase Storage
      const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      
      // Convert base64 to blob
      const audioBlob = this.base64ToBlob(base64Data, `audio/${songData.format || 'wav'}`);
      
      // Create unique file path with better organization
      const timestamp = Date.now();
      const cleanTitle = songData.title?.replace(/[^a-zA-Z0-9_-]/g, '_') || 'song';
      const fileName = `${cleanTitle}_${timestamp}.${songData.format || 'wav'}`;
      const filePath = `songs/${userId}/${songData.gameId}/${fileName}`;
      
      console.log(`📤 UPLOADING AUDIO TO FIREBASE STORAGE: ${filePath}`);
      console.log(`📊 File Size: ${(audioBlob.size / 1024 / 1024).toFixed(2)} MB`);
      
      // Upload to Firebase Storage with metadata
      const storageRef = ref(storage, filePath);
      const uploadResult = await uploadBytes(storageRef, audioBlob, {
        contentType: `audio/${songData.format || 'wav'}`,
        customMetadata: {
          userId: userId,
          gameId: songData.gameId || 'unknown',
          title: songData.title || 'Untitled',
          duration: songData.duration?.toString() || '0',
          sampleRate: songData.sampleRate?.toString() || '44100',
          channels: songData.channels?.toString() || '2',
          uploadTimestamp: timestamp.toString(),
          fileSize: audioBlob.size.toString()
        }
      });
      
      // Get download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      console.log(`✅ AUDIO SUCCESSFULLY UPLOADED TO FIREBASE STORAGE!`);
      console.log(`🔗 Download URL: ${downloadURL}`);
      console.log(`📁 Storage Path: ${filePath}`);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ ERROR UPLOADING TO FIREBASE STORAGE:', error);
      
      // Provide more detailed error information
      if (error instanceof Error) {
        if (error.message.includes('storage/unauthorized')) {
          throw new Error('Firebase Storage access denied. Check authentication and storage rules.');
        } else if (error.message.includes('storage/quota-exceeded')) {
          throw new Error('Firebase Storage quota exceeded.');
        } else if (error.message.includes('storage/invalid-format')) {
          throw new Error('Invalid audio file format.');
        } else if (error.message.includes('CORS')) {
          throw new Error('CORS error - Firebase Storage not configured for this domain.');
        } else {
          throw new Error(`Firebase Storage upload failed: ${error.message}`);
        }
      }
      
      throw new Error(`Failed to upload audio file: ${error}`);
    }
  }

  // Convert base64 string to blob
  private base64ToBlob(base64: string, contentType: string): Blob {
    // Remove data URL prefix if present
    const base64Data = base64.replace(/^data:[^;]+;base64,/, '');
    
    // Convert to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  // Sanitize objects for Firestore (convert custom objects to plain JS objects)
  private sanitizeForFirestore(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      // Convert arrays (including custom Array objects) to plain JS arrays
      return Array.from(obj).map(item => this.sanitizeForFirestore(item));
    }
    
    if (typeof obj === 'object') {
      // Convert objects to plain JS objects
      const sanitized: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          sanitized[key] = this.sanitizeForFirestore(obj[key]);
        }
      }
      return sanitized;
    }
    
    // Return primitive values as-is
    return obj;
  }

  // Format time played for email display
  private formatTimeForEmail(milliseconds: number): string {
    if (milliseconds === 0) return '0 minutes';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes} minutes`;
    } else {
      return 'Less than a minute';
    }
  }

  // Update user's song creation statistics
  private async updateUserSongStats(userId: string): Promise<void> {
    await ensureAnonymousAuth();
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const currentData = userDoc.data();
        await updateDoc(userRef, {
          totalSongsCreated: (currentData.totalSongsCreated || 0) + 1,
          lastSongCreated: new Date(),
        });
        console.log(`📊 Updated user song stats: ${(currentData.totalSongsCreated || 0) + 1} total songs`);
      }
    } catch (error) {
      console.error('❌ ERROR UPDATING USER STATS:', error);
    }
  }

  // Update game statistics in separate collection
  private async updateGameStats(gameId: string, cashEarned: number): Promise<void> {
    await ensureAnonymousAuth();
    const uid = await this.getCurrentUserId();
    if (!uid) return;

    const gameStatRef = doc(db, 'users', uid, 'gameStats', gameId);

    try {
      const docSnap = await getDoc(gameStatRef);
      
      if (docSnap.exists()) {
        // Update existing stats
        const currentStats = docSnap.data() as GameStats;
        await updateDoc(gameStatRef, {
          completions: currentStats.completions + 1,
          lastPlayed: new Date(),
          totalCashEarned: (currentStats.totalCashEarned || 0) + cashEarned,
        });
      } else {
        // Create new game stats document
        await setDoc(gameStatRef, {
          userId: uid, // Keep for potential collectionGroup queries
          gameId,
          completions: 1,
          bestScore: 0,
          totalTimePlayed: 0,
          achievements: [],
          lastPlayed: new Date(),
          totalCashEarned: cashEarned,
        });
      }
    } catch (error) {
      console.error(`Error updating game stats for ${gameId}:`, error);
    }
  }

  // Get user's songs with enhanced filtering and sorting
  async getUserSongs(userId?: string, gameId?: string, limit?: number): Promise<SavedSong[]> {
    const uid = userId || await this.getCurrentUserId();
    if (!uid) return [];

    try {
      console.log(`🎵 FETCHING SONGS for user ${uid}${gameId ? ` from game ${gameId}` : ''}`);
      
      const songsRef = collection(db, 'users', uid, 'songs');
      
      // Try the complex query with ordering first
      try {
        let q = query(songsRef); // No longer need where('userId'...)
        
        // Filter by game if specified
        if (gameId) {
          q = query(q, where('gameId', '==', gameId));
        }
        
        // Sort by creation date (newest first) - this requires a composite index
        q = query(q, orderBy('createdAt', 'desc'));
        
        // Apply limit if specified
        if (limit) {
          q = query(q, limitData(limit));
        }
        
        const querySnapshot = await getDocs(q);
        const songs = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as SavedSong));
        
        console.log(`📊 Found ${songs.length} songs for user (with sorting)`);
        return songs;
        
      } catch (indexError) {
        const errorMessage = indexError instanceof Error ? indexError.message : String(indexError);
        console.warn('⚠️ Composite index not available, falling back to simple query:', errorMessage);
        
        // Fallback to simple query without ordering
        let simpleQuery = query(songsRef); // No longer need where('userId'...)
        
        // Filter by game if specified
        if (gameId) {
          simpleQuery = query(simpleQuery, where('gameId', '==', gameId));
        }
        
        // Apply limit if specified
        if (limit) {
          simpleQuery = query(simpleQuery, limitData(limit));
        }
        
        const querySnapshot = await getDocs(simpleQuery);
        let songs = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as SavedSong));
        
        // Sort client-side since we can't sort on the server without index
        songs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        
        // Apply limit client-side if needed
        if (limit && songs.length > limit) {
          songs = songs.slice(0, limit);
        }
        
        console.log(`📊 Found ${songs.length} songs for user (client-side sorted)`);
        console.log(`💡 To enable server-side sorting, create the Firebase index at: https://console.firebase.google.com/project/musegame-dc6b8/firestore/indexes`);
        
        return songs;
      }
      
    } catch (error) {
      console.error('❌ ERROR FETCHING USER SONGS:', error);
      return [];
    }
  }

  // Get public songs (for community features)
  async getPublicSongs(gameId?: string, limit: number = 20): Promise<SavedSong[]> {
    try {
      console.log(`🌍 FETCHING PUBLIC SONGS${gameId ? ` from game ${gameId}` : ''}`);
      
      const songsRef = collectionGroup(db, 'songs');
      let q = query(songsRef, where('isPublic', '==', true));
      
      // Filter by game if specified
      if (gameId) {
        q = query(q, where('gameId', '==', gameId));
      }
      
      // Sort by creation date (newest first)
      q = query(q, orderBy('createdAt', 'desc'), limitData(limit));
      
      const querySnapshot = await getDocs(q);
      const songs = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as SavedSong));
      
      console.log(`📊 Found ${songs.length} public songs`);
      console.log(`💡 To enable this query, create a Firestore index for the 'songs' collection group.`);
      return songs;
    } catch (error) {
      console.error('❌ ERROR FETCHING PUBLIC SONGS:', error);
      return [];
    }
  }

  // Get a specific song by ID
  async getSong(userId: string, songId: string): Promise<SavedSong | null> {
    try {
      const songRef = doc(db, 'users', userId, 'songs', songId);
      const songDoc = await getDoc(songRef);
      
      if (songDoc.exists()) {
        return {
          ...songDoc.data(),
          id: songDoc.id,
          createdAt: songDoc.data().createdAt?.toDate() || new Date(),
          updatedAt: songDoc.data().updatedAt?.toDate(),
        } as SavedSong;
      }
      
      return null;
    } catch (error) {
      console.error('❌ ERROR FETCHING SONG:', error);
      return null;
    }
  }

  // Update song metadata (title, description, tags, etc.)
  async updateSong(songId: string, updates: Partial<SavedSong>): Promise<void> {
    await ensureAnonymousAuth();
    const uid = await this.getCurrentUserId();
    if (!uid) throw new Error('User not authenticated');

    try {
      const songRef = doc(db, 'users', uid, 'songs', songId);
      
      // Optional: Verify user owns the song before writing to prevent needless writes
      // This should be enforced by Firestore rules primarily
      const songDoc = await getDoc(songRef);
      if (!songDoc.exists()) {
        throw new Error('Song not found or access denied');
      }

      await updateDoc(songRef, {
        ...updates,
        updatedAt: new Date(),
      });

      console.log(`✅ SONG UPDATED: ${songId}`);
    } catch (error) {
      console.error('❌ ERROR UPDATING SONG:', error);
      throw error;
    }
  }

  // Delete a song (metadata and audio file)
  async deleteSong(songId: string): Promise<void> {
    await ensureAnonymousAuth();
    const uid = await this.getCurrentUserId();
    if (!uid) throw new Error('User not authenticated');

    try {
      // Get song data to find the audioUrl for deletion
      const song = await this.getSong(uid, songId);
      if (!song) {
        throw new Error('Song not found or access denied');
      }

      // Delete audio file from storage if it exists
      if (song.audioUrl) {
        try {
          const { getStorage, ref, deleteObject } = await import('firebase/storage');
          const storage = getStorage();
          const fileRef = ref(storage, song.audioUrl);
          await deleteObject(fileRef);
          console.log(`🗑️ DELETED AUDIO FILE: ${song.audioUrl}`);
        } catch (storageError) {
          console.warn('Warning: Could not delete audio file:', storageError);
        }
      }

      // Delete song metadata
      const songRef = doc(db, 'users', uid, 'songs', songId);
      await deleteDoc(songRef);

      // Update user stats
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const currentData = userDoc.data();
        await updateDoc(userRef, {
          totalSongsCreated: Math.max((currentData.totalSongsCreated || 1) - 1, 0),
        });
      }

      console.log(`✅ SONG DELETED: ${songId}`);
    } catch (error) {
      console.error('❌ ERROR DELETING SONG:', error);
      throw error;
    }
  }

  // Get game statistics for user
  async getGameStats(gameId: string): Promise<GameStats | null> {
    const uid = await this.getCurrentUserId();
    if (!uid) return null;

    try {
      const gameStatRef = doc(db, 'users', uid, 'gameStats', gameId);
      const docSnap = await getDoc(gameStatRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as GameStats;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching game stats for ${gameId}:`, error);
      return null;
    }
  }

  // Get game statistics for a specific user (for teachers viewing student data)
  async getUserGameStats(userId: string, gameId: string): Promise<GameStats | null> {
    if (!userId) return null;

    try {
      const gameStatRef = doc(db, 'users', userId, 'gameStats', gameId);
      const docSnap = await getDoc(gameStatRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as GameStats;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching game stats for user ${userId}, game ${gameId}:`, error);
      return null;
    }
  }

  // Get user's current cash amount
  async getMuseCash(): Promise<number> {
    const uid = await this.getCurrentUserId();
    if (!uid) return 0;

    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data().museCash || 0;
    }
    return 0;
  }

  // Get current muse (placeholder)
  async getCurrentMuse(): Promise<number> {
    // This would return the selected muse or muse count
    return 0;
  }



  // Get user's achievements with definitions merged
  async getUserAchievements(userId?: string): Promise<Array<Achievement & Partial<AchievementDefinition>>> {
    const uid = userId || await this.getCurrentUserId();
    if (!uid) return [];

    try {
      const achievementsRef = collection(db, 'users', uid, 'achievements');
      const querySnapshot = await getDocs(achievementsRef);
      
      const userAchievements = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      } as Achievement));

      // Merge with definitions
      const enrichedAchievements = [];
      for (const achievement of userAchievements) {
        const definition = achievement.gameId 
          ? await this.getAchievementDefinition(achievement.gameId, achievement.achievementId)
          : null;
        
        enrichedAchievements.push({
          ...achievement,
          ...definition,
        });
      }

      return enrichedAchievements;
    } catch (error) {
      console.error('Error fetching user achievements:', error);
      return [];
    }
  }
}

// Global instance for games to use
export const gameAPI = new GameAPI();

// Enhanced global function that games can call - now supports onOvertureGameEvent
(window as any).OverturePlatform = {
  // User identity
  getCurrentUserId: () => gameAPI.getCurrentUserId(),

  // Event-based system
  onOvertureGameEvent: (event: GameEventPayload) => gameAPI.onOvertureGameEvent(event),
  
  // Set game context
  setCurrentGame: (gameId: string) => gameAPI.setCurrentGame(gameId),

  // Achievement system
  awardAchievement: (gameId: string, achievementId: string, title?: string, reward?: number) => 
    gameAPI.awardAchievement(gameId, achievementId),
  
  // Enhanced song management
  saveSong: (song: SavedSong) => gameAPI.saveSong(song),
  getUserSongs: (userId?: string, gameId?: string, limit?: number) => gameAPI.getUserSongs(userId, gameId, limit),
  getPublicSongs: (gameId?: string, limit?: number) => gameAPI.getPublicSongs(gameId, limit),
  getSong: (userId: string, songId: string) => gameAPI.getSong(userId, songId),
  updateSong: (songId: string, updates: Partial<SavedSong>) => gameAPI.updateSong(songId, updates),
  deleteSong: (songId: string) => gameAPI.deleteSong(songId),

  // User info
  getMuseCash: () => gameAPI.getMuseCash(),
  getCurrentMuse: () => gameAPI.getCurrentMuse(),
  
  // Game stats
  getGameStats: (gameId: string) => gameAPI.getGameStats(gameId),
  getUserGameStats: (userId: string, gameId: string) => gameAPI.getUserGameStats(userId, gameId),
  
  // Legacy compatibility (can be phased out)
  giveRewards: (gameId: string, rewards: GameReward) => gameAPI.giveRewards(gameId, rewards),
};

// Debug helper - add to window for browser console testing
(window as any).gameAPI = gameAPI;

// Direct bridge for Unity games that call window.onOvertureGameEvent
(window as any).onOvertureGameEvent = (event: GameEventPayload) => {
  console.log('🎮 DIRECT UNITY CALL to onOvertureGameEvent:', event);
  return gameAPI.onOvertureGameEvent(event);
};

// Legacy achievement bridge (in case Unity calls the old way)
(window as any).awardAchievement = (gameId: string, achievementId: string, title?: string, reward?: number) => {
  console.log('🎮 LEGACY UNITY CALL to awardAchievement:', { gameId, achievementId, title, reward });
  return gameAPI.awardAchievement(gameId, achievementId);
};

// Additional bridges for different Unity calling patterns
(window as any).unlockAchievement = (gameId: string, achievementId: string) => {
  console.log('🎮 UNITY CALL to unlockAchievement:', { gameId, achievementId });
  return gameAPI.awardAchievement(gameId, achievementId);
};

// Global saveSong function for Unity games (same pattern as achievements)
(window as any).saveSong = (songData: SavedSong) => {
  console.log('🎮 DIRECT UNITY CALL to saveSong:', songData);
  return gameAPI.saveSong(songData);
};

// Note: Removed console.log interceptor to prevent duplicate achievement triggers
// Unity should call our bridge functions directly instead of relying on log parsing
(window as any).checkUserAchievements = async () => {
  const uid = await gameAPI.getCurrentUserId();
  if (!uid) {
    console.log('❌ No user logged in');
    return;
  }

  try {
    const achievementsRef = collection(db, 'users', uid, 'achievements');
    const querySnapshot = await getDocs(achievementsRef);

    // Enrich with definitions so we can apply daily reset behavior without Unity changes
    const today = new Date();
    const achievementsWithDefs = await Promise.all(
      querySnapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();

        let definition: AchievementDefinition | null = null;
        if (data.gameId && data.achievementId) {
          try {
            definition = await gameAPI.getAchievementDefinition(data.gameId, data.achievementId);
          } catch {
            definition = null;
          }
        }

        return { raw: data, definition };
      })
    );

    // For DAILY achievements (type/frequency), only report them to Unity if they were
    // unlocked TODAY. This lets Unity think "it's locked again" on a new day and
    // call awardAchievement, while the platform still enforces true daily rules.
    const filtered = achievementsWithDefs
      .filter(({ raw, definition }) => {
        const isDailyAchievement =
          !!definition &&
          (definition.frequency === 'daily' || definition.type === 'daily');

        if (!isDailyAchievement) {
          return true;
        }

        const unlockedValue = (raw as any).unlockedAt;
        const unlockedAt =
          unlockedValue && typeof unlockedValue.toDate === 'function'
            ? unlockedValue.toDate()
            : unlockedValue;

        if (!unlockedAt) {
          // If we don't have a date, treat it as "not earned today" and hide it
          return false;
        }

        const unlockedDate =
          unlockedAt instanceof Date ? unlockedAt : new Date(unlockedAt);

        return (
          unlockedDate.getFullYear() === today.getFullYear() &&
          unlockedDate.getMonth() === today.getMonth() &&
          unlockedDate.getDate() === today.getDate()
        );
      })
      .map(({ raw }) => raw);

    console.log(`📊 USER ${uid} HAS ${filtered.length} ACHIEVEMENTS (after daily filter):`);
    filtered.forEach((data: any) => {
      const unlockedAt =
        data.unlockedAt && typeof data.unlockedAt.toDate === 'function'
          ? data.unlockedAt.toDate()
          : data.unlockedAt;
      console.log(
        `  🏆 ${data.achievementId} (${data.title}) - ${data.museCashReward} cash - ${unlockedAt}`
      );
    });

    return filtered;
  } catch (error) {
    console.error('Error fetching user achievements:', error);
  }
};

// (window as any).checkUserAchievements = async (callbackObjectName: string, callbackMethodName: string) => {
//   const uid = await gameAPI.getCurrentUserId();
//   if (!uid) {
//     console.log('❌ No user logged in');
//     return;
//   }

//   try {
//     const achievementsRef = collection(db, 'users', uid, 'achievements');
//     const querySnapshot = await getDocs(achievementsRef);

//     console.log(`📊 USER ${uid} HAS ${querySnapshot.size} ACHIEVEMENTS:`);
//     querySnapshot.forEach(doc => {
//       const data = doc.data();
//       console.log(`  🏆 ${data.achievementId} (${data.title}) - ${data.museCashReward} cash - ${data.unlockedAt?.toDate()}`);
//     });

//     const achievements = querySnapshot.docs.map(doc => doc.data());
//     window.unityInstance.SendMessage(callbackObjectName, callbackMethodName, JSON.stringify(achievements));
//   } catch (error) {
//     console.error('Error fetching user achievements:', error);
//   }
// };

// =============================================================================
// OvertureBridge postMessage Protocol Handlers
// =============================================================================
// Handles communication with Kaplay games using the new postMessage-based protocol.
// This provides better error handling, progress reporting, and timeout recovery
// compared to the legacy injected API approach.

interface OvertureBridgeMessage {
  type: string;
  requestId: string;
  payload?: any;
}

// Send a message back to the game iframe
function sendBridgeMessage(source: MessageEventSource | null, message: OvertureBridgeMessage): void {
  if (source && 'postMessage' in source) {
    try {
      (source as Window).postMessage(message, '*');
    } catch (e) {
      console.warn('[OvertureBridge] Failed to send message:', e);
    }
  }
}

// Handle incoming messages from game iframes
window.addEventListener('message', async (event: MessageEvent) => {
  const data = event.data;

  // Only handle OVERTURE_* messages
  if (!data || typeof data.type !== 'string' || !data.type.startsWith('OVERTURE_')) {
    return;
  }

  const { type, requestId, payload } = data as OvertureBridgeMessage;
  const source = event.source;

  console.log(`[OvertureBridge] Received ${type}:`, { requestId, payload });

  switch (type) {
    case 'OVERTURE_HANDSHAKE_REQUEST': {
      // Respond with platform capabilities
      sendBridgeMessage(source, {
        type: 'OVERTURE_HANDSHAKE_RESPONSE',
        requestId,
        payload: {
          supported: true,
          capabilities: ['saveSong', 'achievements', 'progress'],
          version: '1.0.0',
        },
      });
      console.log('[OvertureBridge] Handshake completed with game:', payload?.gameId);
      break;
    }

    case 'OVERTURE_SAVE_SONG': {
      // Send acknowledgment immediately
      sendBridgeMessage(source, {
        type: 'OVERTURE_SAVE_SONG_ACK',
        requestId,
      });

      try {
        // Report progress: Starting
        sendBridgeMessage(source, {
          type: 'OVERTURE_SAVE_SONG_PROGRESS',
          requestId,
          payload: { percent: 10, stage: 'Processing audio data' },
        });

        // Validate payload
        if (!payload || !payload.audioData) {
          throw new Error('Missing audio data in save request');
        }

        // Report progress: Uploading
        sendBridgeMessage(source, {
          type: 'OVERTURE_SAVE_SONG_PROGRESS',
          requestId,
          payload: { percent: 30, stage: 'Uploading to storage' },
        });

        // Save the song using the existing GameAPI
        const songId = await gameAPI.saveSong({
          title: payload.title || 'Untitled Song',
          gameId: payload.gameId || 'unknown',
          audioData: payload.audioData,
          format: payload.format || 'wav',
          duration: payload.duration,
          sampleRate: payload.sampleRate,
          channels: payload.channels,
          fileSize: payload.fileSize,
          bpm: payload.bpm,
          tags: payload.tags,
          description: payload.description,
          isPublic: payload.isPublic || false,
          createdAt: new Date(),
        });

        // Report progress: Complete
        sendBridgeMessage(source, {
          type: 'OVERTURE_SAVE_SONG_PROGRESS',
          requestId,
          payload: { percent: 100, stage: 'Complete' },
        });

        // Send success response
        sendBridgeMessage(source, {
          type: 'OVERTURE_SAVE_SONG_RESPONSE',
          requestId,
          payload: {
            success: true,
            songId,
          },
        });

        console.log(`[OvertureBridge] Song saved successfully: ${songId}`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[OvertureBridge] Save song failed:', errorMessage);

        // Send error response
        sendBridgeMessage(source, {
          type: 'OVERTURE_SAVE_SONG_RESPONSE',
          requestId,
          payload: {
            success: false,
            error: errorMessage,
          },
        });
      }
      break;
    }

    case 'OVERTURE_AWARD_ACHIEVEMENT': {
      // Send acknowledgment immediately
      sendBridgeMessage(source, {
        type: 'OVERTURE_AWARD_ACHIEVEMENT_ACK',
        requestId,
      });

      try {
        const gameId = String(payload?.gameId || 'unknown');
        const achievementId = String(payload?.achievementId || '').trim();
        if (!achievementId) {
          throw new Error('Missing achievementId in award request');
        }

        const awarded = await gameAPI.awardAchievement(gameId, achievementId);

        sendBridgeMessage(source, {
          type: 'OVERTURE_AWARD_ACHIEVEMENT_RESPONSE',
          requestId,
          payload: { success: true, awarded },
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[OvertureBridge] Award achievement failed:', errorMessage);
        sendBridgeMessage(source, {
          type: 'OVERTURE_AWARD_ACHIEVEMENT_RESPONSE',
          requestId,
          payload: { success: false, error: errorMessage },
        });
      }
      break;
    }

    default:
      console.warn(`[OvertureBridge] Unknown message type: ${type}`);
  }
});

console.log('[OvertureBridge] Platform message handlers initialized');
